import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import {
  EllipsisMenuCopyItem,
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import {
  hideNotification,
  showLoadingNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { useCallback, useMemo } from 'react';
import {
  EllipsisMenuAuditEffectsItem,
  EllipsisMenuSourceDownloadItem,
  HiddenFeatureIndicator,
} from '../../../components';
import { useJournalEntriesModal } from '../../../modals';
import {
  useHasReservationUserDataEditAccess,
  useReservationRefetchMutation,
} from '../_hooks';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';
import { useReservationDetailDrawer } from '../useReservationDetailDrawer';

type Props = {
  reservation: Reservation;
  openPostingDateModal: () => void;
  openDeleteModal: () => void;
};

export const ReservationEllipsisMenuItems = ({
  reservation,
  openPostingDateModal,
  openDeleteModal,
}: Props) => {
  const { setView } = useReservationDetailDrawer();
  const hasUserDataEditAccess = useHasReservationUserDataEditAccess();
  const { open: openJournalEntriesModal } = useJournalEntriesModal();
  const invalidate = useInvalidateApiQueries();

  const {
    mutateAsync: refreshJournalEntries,
    isPending: loadingRefreshJournalEntries,
  } = useApiMutation('post', '/reservations/journal-entries');

  const { mutateAsync: mutate, isPending: loadingUpdate } = useApiMutation(
    'put',
    '/reservations/{id}'
  );
  const {
    mutateAsync: updateGeneralLedgerStatus,
    isPending: loadingGeneralLedgerStatus,
  } = useApiMutation('patch', '/reservations/{id}/status');

  const refreshJEs = useCallback(() => {
    showLoadingNotification({
      id: reservation.id,
    });

    refreshJournalEntries({
      body: {
        ids: [reservation.id],
      },
    })
      .then(() => {
        updateSuccessNotification({
          id: reservation.id,
          title: 'Journal entries refreshed',
          message: 'Journal entries have been refreshed successfully',
        });
        invalidate(['reservations']);
      })
      .catch((error: any) => {
        showApiErrorNotification({
          id: reservation.id,
          error,
          title: 'Failed to refresh journal entries',
          defaultMessage:
            'We failed to refresh journal entries. Please try again and contact support if the issue persists.',
        });
      });
  }, [reservation.id, invalidate, refreshJournalEntries]);

  const toggleCancellation = useCallback(async () => {
    const notifyId = `toggle-cancellation-${reservation.id}`;
    showLoadingNotification({
      id: notifyId,
    });

    await mutate({
      params: {
        path: {
          id: reservation.id,
        },
      },
      body: {
        status: reservation.status === 'canceled' ? 'booked' : 'cancelled',
      },
    })
      .then(() => {
        invalidate(['reservations']);
      })
      .catch((error: any) => {
        console.error(error);
        showApiErrorNotification({
          title: 'Failed to update the reservation',
          error,
          defaultMessage:
            'We failed to update the reservation. Please try again and contact support if the issue persists.',
        });
      })
      .finally(() => {
        hideNotification({
          id: notifyId,
        });
      });
  }, [reservation.id, reservation.status, invalidate, mutate]);

  const toggleArchiveStatus = useCallback(async () => {
    const notifyId = `toggle-general-ledger-status-${reservation.id}`;

    showLoadingNotification({
      id: notifyId,
    });

    await updateGeneralLedgerStatus({
      params: {
        path: {
          id: reservation.id,
        },
      },
      body: {
        generalLedgerStatus:
          reservation.generalLedgerStatus === 'inactive'
            ? 'active'
            : 'inactive',
      },
    })
      .then(() => {
        invalidate(['reservations']);
        updateSuccessNotification({
          id: notifyId,
          title:
            reservation.generalLedgerStatus === 'inactive'
              ? 'Reservation un-archived'
              : 'Reservation archived',
          message:
            reservation.generalLedgerStatus === 'inactive'
              ? 'The reservation was successfully included in accounting.'
              : 'The reservation was successfully excluded from accounting.',
        });
      })
      .catch((error: any) => {
        showApiErrorNotification({
          id: notifyId,
          title: 'Failed to update reservation status',
          error,
          defaultMessage:
            'We failed to update the reservation. Please try again and contact support if the issue persists.',
        });
      });
  }, [
    reservation.id,
    reservation.generalLedgerStatus,
    invalidate,
    updateGeneralLedgerStatus,
  ]);

  // disblable actions when locked: overwrite posting date, refresh JE, refetch res, archive, cancel/revert
  const isLocked = reservation.lock?.status === 'locked';
  const lockTooltip = isLocked ? reservation.lock?.message : undefined;

  return (
    <>
      {hasUserDataEditAccess && (
        <EllipsisMenuItem
          onClick={() => setView('edit')}
          customIcon={<Icon icon="Edit3Icon" size={16} />}
        >
          Edit user data
        </EllipsisMenuItem>
      )}

      <EllipsisMenuItem
        onClick={openPostingDateModal}
        customIcon={<Icon icon="CalendarEventIcon" size={16} />}
        disabled={isLocked}
        tooltip={lockTooltip}
      >
        Overwrite posting date
      </EllipsisMenuItem>

      <EllipsisMenuItem
        onClick={() =>
          openJournalEntriesModal({
            reservationIds: [reservation.id],
          })
        }
        customIcon={<Icon icon="ListUnorderedIcon" size={16} />}
      >
        Journal entries
      </EllipsisMenuItem>

      <EllipsisMenuDivider />

      <EllipsisMenuItem
        customIcon={<Icon icon="RefreshCwIcon" size={16} />}
        onClick={refreshJEs}
        loading={loadingRefreshJournalEntries}
        disabled={isLocked}
        tooltip={lockTooltip}
      >
        Refresh journal entries
      </EllipsisMenuItem>

      {!reservation.isManualReservation && (
        <ReservationRefetchEllipsisItem
          appId={reservation.connection?.appId}
          connectionId={reservation.connection?.id}
          uniqueRef={reservation.uniqueRef}
          refreshType="show-notification"
          disabled={isLocked}
          tooltip={lockTooltip}
        />
      )}

      {(reservation.confirmationCode || reservation.pmsReferenceCode) && (
        <>
          <EllipsisMenuDivider />

          {reservation.confirmationCode && (
            <EllipsisMenuCopyItem value={reservation.confirmationCode}>
              Copy confirmation code
            </EllipsisMenuCopyItem>
          )}
          {reservation.pmsReferenceCode && (
            <EllipsisMenuCopyItem value={reservation.pmsReferenceCode}>
              Copy PMS code
            </EllipsisMenuCopyItem>
          )}
        </>
      )}

      <EllipsisMenuDivider />
      <EllipsisMenuItem
        loading={loadingGeneralLedgerStatus}
        onClick={toggleArchiveStatus}
        icon={
          reservation.generalLedgerStatus === 'inactive'
            ? 'RefreshCwIcon'
            : 'EyeSlashIcon'
        }
        disabled={isLocked}
        tooltip={lockTooltip}
      >
        {reservation.generalLedgerStatus === 'inactive'
          ? 'Re-enable reservation'
          : 'Archive reservation'}
      </EllipsisMenuItem>

      {reservation.isManualReservation && (
        <EllipsisMenuDangerItem
          disabled={isLocked}
          tooltip={lockTooltip}
          onClick={openDeleteModal}
        >
          Delete
        </EllipsisMenuDangerItem>
      )}

      <HiddenFeatureIndicator permission="vrp-admin">
        <EllipsisMenuAuditEffectsItem entityId={reservation.id} />

        <EllipsisMenuItem
          loading={loadingUpdate}
          onClick={toggleCancellation}
          customIcon={<Icon icon="AlertTriangleIcon" size={16} />}
          disabled={isLocked}
          tooltip={lockTooltip}
        >
          {reservation.status === 'canceled'
            ? 'Revert cancellation'
            : 'Cancel reservation'}
        </EllipsisMenuItem>

        <EllipsisMenuCopyItem value={reservation.id} />

        <EllipsisMenuSourceDownloadItem
          fileName={reservation.uniqueRef || reservation.id}
          where={{ reservation: { id: { _eq: reservation.id } } }}
        />
      </HiddenFeatureIndicator>
    </>
  );
};

export const ReservationRefetchEllipsisItem = (
  props: Parameters<
    ReturnType<typeof useReservationRefetchMutation>['mutate']
  >[0] & {
    disabled?: boolean;
    tooltip?: string;
  }
) => {
  const { loading, mutate } = useReservationRefetchMutation();

  const extract = useCallback(() => {
    mutate(props);
  }, [mutate, props]);

  const disabled = useMemo(() => {
    return (
      props.disabled ||
      !props.uniqueRef ||
      !props.connectionId ||
      !props.appId ||
      props.appId === 'finalytic'
    );
  }, [props.uniqueRef, props.connectionId, props.appId, props.disabled]);

  return (
    <EllipsisMenuItem
      customIcon={<Icon icon={'RefreshCwIcon'} size={16} />}
      loading={loading}
      disabled={disabled}
      onClick={extract}
      tooltip={props.tooltip}
    >
      Refetch reservation
    </EllipsisMenuItem>
  );
};
